import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Hotspot {
  x: number;
  y: number;
  materialId: string;
}

interface GalleryImage {
  url?: string;
  caption?: string;
  hotspots?: Hotspot[];
}

interface GalleryBlockProps {
  content: {
    title?: string;
    images?: GalleryImage[];
  };
}

interface MaterialInfo {
  id: string;
  name: string;
  image_url: string | null;
  category: string;
}

export function GalleryBlock({ content }: GalleryBlockProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const allMaterialIds = (content.images || [])
    .flatMap((img) => (img.hotspots || []).map((h) => h.materialId))
    .filter(Boolean);
  const uniqueIds = [...new Set(allMaterialIds)];

  const { data: materials } = useQuery({
    queryKey: ["gallery-hotspot-materials", uniqueIds],
    enabled: uniqueIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("id, name, image_url, category")
        .in("id", uniqueIds);
      if (error) throw error;
      return data as MaterialInfo[];
    },
  });

  const materialMap = new Map((materials || []).map((m) => [m.id, m]));

  if (!content.images?.length) return null;

  return (
    <section className="py-16">
      {content.title && (
        <h2 className="mx-auto max-w-[1200px] px-6 font-serif text-2xl md:text-3xl text-foreground mb-8">
          {content.title}
        </h2>
      )}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto px-6 pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {content.images.map((img, i) => (
          <GalleryImageWithHotspots key={i} img={img} index={i} materialMap={materialMap} />
        ))}
      </div>

      <style>{`
        @keyframes gallery-hotspot-ping {
          0% { transform: scale(1); opacity: 0.6; }
          70% { transform: scale(2.5); opacity: 0; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `}</style>
    </section>
  );
}

function GalleryImageWithHotspots({
  img,
  index,
  materialMap,
}: {
  img: GalleryImage;
  index: number;
  materialMap: Map<string, MaterialInfo>;
}) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const hotspots = img.hotspots || [];

  return (
    <div className="flex-none w-[85vw] md:w-[60vw] lg:w-[45vw] snap-start group">
      {img.url && (
        <div className="relative overflow-hidden aspect-[4/3]">
          <img
            src={img.url}
            alt={img.caption || `Gallery image ${index + 1}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          {hotspots.map((spot, i) => {
            const mat = materialMap.get(spot.materialId);
            const isActive = activeIdx === i;
            const labelOnLeft = spot.x > 65;

            return (
              <div
                key={i}
                className="absolute"
                style={{
                  left: `${spot.x}%`,
                  top: `${spot.y}%`,
                  transform: "translate(-50%, -50%)",
                }}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setActiveIdx(activeIdx === i ? null : i);
                }}
              >
                <span
                  className="absolute inset-0 -m-2 rounded-full border border-white/40 animate-[gallery-hotspot-ping_2.5s_ease-out_infinite]"
                  style={{ animationDelay: `${i * 0.4}s` }}
                />
                <span
                  className={`absolute -inset-1.5 rounded-full bg-white/20 backdrop-blur-sm transition-all duration-300 ${
                    isActive ? "scale-[2.2] bg-white/30" : "scale-100"
                  }`}
                />
                <span
                  className={`relative z-10 block h-3 w-3 rounded-full border-2 border-white bg-primary shadow-[0_0_8px_rgba(0,0,0,0.3)] transition-all duration-300 cursor-pointer ${
                    isActive ? "scale-150 shadow-[0_0_16px_rgba(0,0,0,0.4)]" : ""
                  }`}
                />

                {mat && (
                  <Link
                    to="/components"
                    className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-2 transition-all duration-300 ${
                      labelOnLeft ? "right-8 flex-row-reverse" : "left-8"
                    } ${
                      isActive
                        ? "opacity-100 translate-x-0"
                        : labelOnLeft
                          ? "opacity-0 translate-x-2 pointer-events-none"
                          : "opacity-0 -translate-x-2 pointer-events-none"
                    }`}
                  >
                    <span className="block h-px w-4 bg-white/60" />
                    <span className="flex items-center gap-2.5 rounded-lg bg-background/95 px-2.5 py-2 font-sans text-xs font-medium text-foreground shadow-lg backdrop-blur-md border border-border/50">
                      {mat.image_url && (
                        <img
                          src={mat.image_url}
                          alt={mat.name}
                          className="h-9 w-9 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <span className="flex flex-col gap-0.5">
                        <span className="whitespace-nowrap font-medium">{mat.name}</span>
                        <span className="whitespace-nowrap text-[10px] text-muted-foreground">{mat.category}</span>
                      </span>
                    </span>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
      {img.caption && (
        <p className="mt-3 font-sans text-xs text-muted-foreground max-w-[90%]">
          {img.caption}
        </p>
      )}
    </div>
  );
}
